/**
 * Store operating hours utilities.
 * Day keys: "0" = Sunday, "1" = Monday, ..., "6" = Saturday
 * Times in 24h format "HH:mm"
 *
 * Special case: open "00:00" and close "00:00" = store is open 24 hours for that day.
 * Always-open: omit operatingHours (DB null) or a JSON object with no day keys — not the same
 * as saving all days closed (object with day keys "0"–"6" and each day null / no window).
 */

export type DaySchedule = {
  open: string;
  close: string;
};

export type OperatingHours = Record<string, DaySchedule | null>;

const DAY_KEYS = ['0', '1', '2', '3', '4', '5', '6'] as const;

/**
 * Parse operating hours from store JSON.
 * Returns null if not configured or empty (store always open).
 * Returns a week object (possibly all-null days) when the JSON explicitly includes at least
 * one day key "0"–"6" — including “closed every day” from the panel.
 */
export function parseOperatingHours(
  raw: unknown
): OperatingHours | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const obj = raw as Record<string, unknown>;
  const result: OperatingHours = {};

  for (const day of DAY_KEYS) {
    const dayVal = obj[day];
    if (dayVal === null || dayVal === undefined) {
      result[day] = null;
      continue;
    }
    if (typeof dayVal !== 'object' || dayVal === null) {
      result[day] = null;
      continue;
    }
    const d = dayVal as Record<string, unknown>;
    const open = typeof d.open === 'string' ? d.open : null;
    const close = typeof d.close === 'string' ? d.close : null;
    if (open && close) {
      result[day] = { open, close };
    } else {
      result[day] = null;
    }
  }

  const hasAnySchedule = Object.values(result).some(Boolean);
  if (hasAnySchedule) {
    return result;
  }

  const hasExplicitDayKey = DAY_KEYS.some((day) =>
    Object.prototype.hasOwnProperty.call(obj, day)
  );
  return hasExplicitDayKey ? result : null;
}

/** "H:mm" or "HH:mm" to minutes since midnight; null when the value is not a valid time. */
function parseTimeOfDay(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/**
 * Minutes the store is open per week under the same rules as {@link isStoreOpen}: "00:00"–"00:00"
 * is 24 h, a close before the open crosses midnight and stays on the day it opens, an equal open
 * and close (other than 00:00) is never open, and a null day is closed. Returns null when any time
 * is not "H:mm", so a malformed schedule never turns into a number.
 */
export function weeklyOpenMinutes(operatingHours: OperatingHours): number | null {
  let total = 0;
  for (const day of DAY_KEYS) {
    const schedule = operatingHours[day];
    if (!schedule) continue;
    const open = parseTimeOfDay(schedule.open);
    const close = parseTimeOfDay(schedule.close);
    if (open === null || close === null) return null;
    if (open === 0 && close === 0) total += 24 * 60;
    else if (open < close) total += close - open;
    else if (open > close) total += 24 * 60 - open + close;
  }
  return total;
}

/** Default timezone when store has none set (Brazil's main zone). */
export const DEFAULT_STORE_TIMEZONE = 'America/Sao_Paulo';

/**
 * Returns a valid IANA timezone for date math. Invalid or empty values (common in bad imports)
 * would make `Intl.DateTimeFormat` throw RangeError and crash Server Components.
 */
export function resolveStoreTimezone(
  timezone: string | null | undefined
): string {
  const fallback = DEFAULT_STORE_TIMEZONE;
  if (timezone == null || typeof timezone !== 'string') {
    return fallback;
  }
  const trimmed = timezone.trim();
  if (!trimmed) {
    return fallback;
  }
  try {
    Intl.DateTimeFormat('en-US', { timeZone: trimmed }).format(new Date());
    return trimmed;
  } catch {
    return fallback;
  }
}

/** Brazil's 4 IANA timezones for store timezone selection. */
export const BRAZIL_TIMEZONES: { value: string; labelKey: string }[] = [
  { value: 'America/Sao_Paulo', labelKey: 'timezoneSaoPaulo' },
  { value: 'America/Manaus', labelKey: 'timezoneManaus' },
  { value: 'America/Cuiaba', labelKey: 'timezoneCuiaba' },
  { value: 'America/Noronha', labelKey: 'timezoneNoronha' },
];

/**
 * Get day (0-6) and time (minutes since midnight) in a given timezone.
 * Used to interpret operating hours in the store's local time.
 */
function getDayAndMinutesInTimezone(
  now: Date,
  timezone: string
): { day: number; minutes: number } {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    weekday: 'short',
  });
  const parts = formatter.formatToParts(now);
  const weekday = parts.find((p) => p.type === 'weekday')?.value ?? 'Sun';
  const hour = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10);
  const minute = parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0', 10);
  const weekdayToNum: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  return {
    day: weekdayToNum[weekday] ?? 0,
    minutes: hour * 60 + minute,
  };
}

/**
 * Check if store is currently open based on operating hours.
 * Operating hours are interpreted in the store's timezone (e.g. America/Sao_Paulo).
 * This ensures correct behavior when users in different Brazilian states browse the store.
 * @param operatingHours - Parsed operating hours or null (null = always open)
 * @param now - Optional Date for testing; defaults to current time
 * @param storeTimezone - IANA timezone (e.g. America/Sao_Paulo). Defaults to America/Sao_Paulo.
 */
export function isStoreOpen(
  operatingHours: OperatingHours | null,
  now: Date = new Date(),
  storeTimezone: string = DEFAULT_STORE_TIMEZONE
): boolean {
  if (!operatingHours) {
    return true;
  }

  const tz = resolveStoreTimezone(storeTimezone);
  const { day, minutes: currentMinutes } = getDayAndMinutesInTimezone(now, tz);
  const schedule = operatingHours[String(day)];

  if (!schedule) {
    return false;
  }

  const [openH, openM] = schedule.open.split(':').map(Number);
  const [closeH, closeM] = schedule.close.split(':').map(Number);
  const openMinutes = openH * 60 + openM;
  const closeMinutes = closeH * 60 + closeM;

  // 00:00 to 00:00 = open 24 hours for that day
  if (openMinutes === 0 && closeMinutes === 0) {
    return true;
  }

  if (openMinutes <= closeMinutes) {
    return currentMinutes >= openMinutes && currentMinutes < closeMinutes;
  }
  // Overnight: e.g. 22:00 - 02:00
  return currentMinutes >= openMinutes || currentMinutes < closeMinutes;
}

/** Why the consumer cannot order right now. `null` = store is open. */
export type StoreClosedReason = 'temporarilyClosed' | 'outsideOperatingHours';

/** Store fields that decide whether the consumer can order — as returned by GET /api/stores/[id]. */
export type StoreAvailability = {
  operatingHours?: unknown;
  timezone?: string | null;
  temporarilyClosed?: boolean | null;
};

/**
 * Single source of truth for "is this store taking orders right now".
 * The manual pause (panel button) wins over the weekly grid, so a lojista can close the store
 * without touching the schedule; with the pause off the grid decides, and a store with no grid
 * configured stays always open.
 */
export function getStoreClosedReason(
  store: StoreAvailability | null | undefined,
  now: Date = new Date()
): StoreClosedReason | null {
  if (!store) {
    return null;
  }
  if (store.temporarilyClosed) {
    return 'temporarilyClosed';
  }
  const hours = parseOperatingHours(store.operatingHours);
  if (!hours) {
    return null;
  }
  return isStoreOpen(hours, now, resolveStoreTimezone(store.timezone))
    ? null
    : 'outsideOperatingHours';
}

/** Convenience wrapper over {@link getStoreClosedReason} for call sites that only need a boolean. */
export function isStoreClosedNow(
  store: StoreAvailability | null | undefined,
  now: Date = new Date()
): boolean {
  return getStoreClosedReason(store, now) !== null;
}

/**
 * Grid equivalent to "always open": every day 24h ("00:00"-"00:00").
 * Seeds the panel form for a store with no schedule saved (Energia S.A. and other 24h
 * exceptions), so ticking "usar horário de funcionamento" never narrows the store on its own.
 */
export function getAlwaysOpenOperatingHours(): OperatingHours {
  return {
    '0': { open: '00:00', close: '00:00' },
    '1': { open: '00:00', close: '00:00' },
    '2': { open: '00:00', close: '00:00' },
    '3': { open: '00:00', close: '00:00' },
    '4': { open: '00:00', close: '00:00' },
    '5': { open: '00:00', close: '00:00' },
    '6': { open: '00:00', close: '00:00' },
  };
}

/**
 * Day names for display (locale-aware can be passed later).
 */
export const DAY_NAMES: Record<string, string> = {
  '0': 'Domingo',
  '1': 'Segunda-feira',
  '2': 'Terça-feira',
  '3': 'Quarta-feira',
  '4': 'Quinta-feira',
  '5': 'Sexta-feira',
  '6': 'Sábado',
};
